import React, { useRef } from "react";
import { Link } from "react-router-dom";
import {
  FormulationIcon,
  IonizableLipidsIcon,
  PublicationsIcon,
  QuoteIcon,
} from "../components/icons";
import PublicationsTable from "../components/PublicationsTable";
import Navbar from "../components/ui/navbar";
import Footer from "../components/ui/Footer";
import StatsCard from "../components/ui/StatsCard";
import { useResearchData } from "../hooks/useResearchData";
import { useScrollToHash } from "../hooks/useScrollToHash";

const Main: React.FC = () => {
  const { projects, totalCount, isLoading, error } = useResearchData();
  const publicationsRef = useRef<HTMLDivElement>(null);

  useScrollToHash(publicationsRef, "#publications");

  return (
    <>
      <div className="relative flex-shrink-0 w-full text-white bg-blue-900 border-b border-1" style={{ height: '74px' }}>
        <Navbar />
      </div>
      <main className="flex-1">
        <section className="text-black">
          <div className="max-w-screen-xl px-4 py-8 mx-auto text-center lg:py-16 lg:px-12">
            <h1 className="py-4 text-6xl font-extrabold leading-none tracking-tight text-gray-900 md:text-7xl lg:text-9xl">
              LNPDB
            </h1>
            <p className="py-4 pb-6 text-xl font-normal text-gray-500 lg:text-2xl sm:px-16 xl:px-48">
              Lipid Nanoparticle Database Towards Structure-Function Modeling
              and Data-driven Design for Nucleic Acid Delivery
            </p>

            <div className="inline-flex items-center justify-center w-full">
              <hr className="w-64 h-1 my-8 bg-gray-200 border-0 rounded" />
              <QuoteIcon />
            </div>

            <p className="py-4 pt-6 text-xl text-gray-700 text-bold">
              Digital framework for analyzing and modeling lipid nanoparticles
            </p>
            <div className="flex flex-col items-center w-full">
              <p className="w-4/5 text-gray-500 text-balance">
                LNPDB is an integrated database and web tool that compiles
                structure-function data for {totalCount.lnp_count} LNP
                formulations, representing {totalCount.unique_count} unique
                ionizable lipids across {projects.length} publications and 1
                commercial supplier. LNPDB supports future data contributions
                for continued growth.
              </p>
            </div>

            <div className="px-4 mx-auto my-12 text-center md:max-w-screen-md lg:max-w-screen-lg lg:px-36">
              <div className="flex flex-wrap items-center justify-center mt-8 text-gray-500 sm:justify-between">
                <StatsCard
                  icon={<FormulationIcon />}
                  title="Formulations"
                  count={totalCount?.lnp_count}
                  linkTo="/search/text"
                />

                <StatsCard
                  icon={<IonizableLipidsIcon />}
                  title="Unique Ionizable Lipids"
                  count={totalCount?.unique_count}
                  linkTo="/ionizable"
                />

                <StatsCard
                  icon={<PublicationsIcon />}
                  title="Publications"
                  count={projects.length}
                  linkTo="#publications"
                />
              </div>
            </div>

            <div className="flex justify-center gap-8">
              <div className="flex flex-col my-4 space-y-4 lg:mb-16 sm:flex-row sm:justify-center sm:space-y-0 sm:space-x-4">
                <Link
                  to="/search/text"
                  className="inline-flex items-center justify-center px-5 py-3 text-base font-medium text-center text-white rounded-lg bg-primary-700 hover:bg-primary-800 focus:ring-4 focus:ring-primary-300"
                >
                  Explore
                </Link>
              </div>
              <div className="flex flex-col my-4 space-y-4 lg:mb-16 sm:flex-row sm:justify-center sm:space-y-0 sm:space-x-4">
                <Link
                  to="/search/structure"
                  className="inline-flex items-center justify-center px-5 py-3 text-base font-medium text-center bg-white rounded-lg text-primary-700 hover:bg-gray-100 hover:ring-2 ring-1 ring-primary-300 hover:ring-gray-200"
                >
                  Search
                </Link>
              </div>
            </div>

            <div
              id="publications"
              ref={publicationsRef}
              className="flex items-start justify-between px-10 pt-10 scroll-mt-10"
            >
              <div className="w-full">
                <p className="pb-6 text-xl font-bold text-center text-gray-800">
                  LNP Libraries
                </p>
                <p className="pb-10 pl-10 text-center text-balance">
                  LNPDB integrates data from {projects.length} publications that
                  feature unique lipid nanoparticle libraries for nucleic acid
                  delivery. For each library, LNPDB standardizes lipid
                  nanoparticle featurization by encoding lipid composition,
                  experimental methods, and functional results, and generates
                  CHARMM force field files for constituent lipids to enable
                  molecular dynamics simulations.
                </p>

                <div className="w-full px-10">
                  {isLoading ? (
                    <p className="text-center text-gray-500">
                      Loading publications...
                    </p>
                  ) : error ? (
                    <p className="text-center text-red-500">
                      Error loading publications: {error}
                    </p>
                  ) : projects.length > 0 ? (
                    <PublicationsTable projects={projects} />
                  ) : (
                    <p className="text-center text-gray-500">
                      No publications available.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default Main;
